import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {
  dashboardGeneticsRetrieveStateDefaults,
  DashboardGeneticsRetrieveStateModel,
  dashboardGeneticsRetrieveUniqueStateId,
} from './dashboard-genetics-retrieve.models';
import {
  GetGeneticStatus,
  GetGeneticStatusFail,
  GetGeneticStatusSuccess,
  GetOwnGeneticStatus,
  GetOwnGeneticStatusFail,
  GetOwnGeneticStatusSuccess,
  LoadGeneticData,
  LoadGeneticDataFail,
  LoadGeneticDataSuccess,
} from './dashboard-genetics-retrieve.actions';
import {GeneticFileProcessingStatus, ProcessedGeneticFileStatus} from '../../types/dashboard-genetics-processing.types';
import {DashboardGeneticsRetrieveService} from '../../services/dashboard-genetics-retrieve/dashboard-genetics-retrieve.service';
import {BackendError} from '../../../messages/messages.actions';
import {PROGRESS_STATUSES, UnknownError} from '../../../shared/symbols';
import {AuthState} from '../../../auth/states/auth/auth.state';

@State<DashboardGeneticsRetrieveStateModel>({
  name: dashboardGeneticsRetrieveUniqueStateId,
  defaults: dashboardGeneticsRetrieveStateDefaults,
})
@Injectable()
export class DashboardGeneticsRetrieveState {
  constructor(private geneticsRetrieve: DashboardGeneticsRetrieveService, private store: Store) {}

  @Selector()
  static getStatusProgress(state: DashboardGeneticsRetrieveStateModel): PROGRESS_STATUSES {
    return state.getStatusProgress;
  }

  @Selector()
  static lastGetStatusError(state: DashboardGeneticsRetrieveStateModel): UnknownError {
    return state.lastGetStatusError;
  }

  @Selector()
  static mostActualStatus(state: DashboardGeneticsRetrieveStateModel): DashboardGeneticsRetrieveStateModel['mostActualStatus'] {
    return state.mostActualStatus;
  }

  @Selector()
  static mostActualOwnStatus(state: DashboardGeneticsRetrieveStateModel): DashboardGeneticsRetrieveStateModel['mostActualStatus'] {
    return state.mostActualOwnStatus;
  }

  @Selector()
  static hasGeneticData(state: DashboardGeneticsRetrieveStateModel): boolean {
    return !!state.mostActualStatus;
  }

  @Selector()
  static hasOwnGeneticData(state: DashboardGeneticsRetrieveStateModel): boolean {
    return !!state.mostActualOwnStatus;
  }

  @Selector()
  static loadGeneticsProgress(state: DashboardGeneticsRetrieveStateModel): DashboardGeneticsRetrieveStateModel['loadGeneticsProgress'] {
    return state.loadGeneticsProgress;
  }

  @Selector()
  static loadGeneticsError(state: DashboardGeneticsRetrieveStateModel): DashboardGeneticsRetrieveStateModel['loadGeneticsError'] {
    return state.loadGeneticsError;
  }

  @Selector()
  static geneticData(state: DashboardGeneticsRetrieveStateModel): DashboardGeneticsRetrieveStateModel['geneticData'] {
    return state.geneticData;
  }

  /**
   * Extract the most actual status from a status list.
   * At first filter the highest status, then choose the latest one by date.
   */
  static extractMostActualStatus(statusList: GeneticFileProcessingStatus[]): GeneticFileProcessingStatus {
    // Set available statuses in a list from the latest one.
    const availableStatusesByPriority: ProcessedGeneticFileStatus[] = [
      ProcessedGeneticFileStatus.EXTRACTED,
      ProcessedGeneticFileStatus.CONVERTED,
      ProcessedGeneticFileStatus.UPLOADED,
    ];

    // Find the most recent item with the highest status code.
    for (const statusCode of availableStatusesByPriority) {
      // Form list of items with this status code.
      const sameStatusCode = statusList.filter(item => item.pipeline_type === statusCode);
      if (sameStatusCode.length) {
        // Return the latest item by date.
        return sameStatusCode.sort((a, b) => new Date(b.added_at).getTime() - new Date(a.added_at).getTime())[0];
      }
    }

    // List is empty => return null.
    return null;
  }

  @Action(GetOwnGeneticStatus)
  getOwnGeneticStatus(ctx: StateContext<DashboardGeneticsRetrieveStateModel>) {
    const currentUserId = this.store.selectSnapshot(AuthState.getUser)?.uid;

    if (!currentUserId) {
      return;
    }

    ctx.patchState({
      getOwnStatusProgress: PROGRESS_STATUSES.IN_PROGRESS,
      lastGetOwnStatusError: null,
      isPublicGenetics: false,
      mostActualOwnStatus: null,
    });

    this.geneticsRetrieve.getGeneticStatuses(currentUserId, false).subscribe({
      next: geneticData => ctx.dispatch(new GetOwnGeneticStatusSuccess(currentUserId, geneticData)),
      error: error => ctx.dispatch(new GetOwnGeneticStatusFail(error)),
    });
  }

  @Action(GetOwnGeneticStatusSuccess)
  getOwnGeneticStatusSuccess(
    ctx: StateContext<DashboardGeneticsRetrieveStateModel>,
    {userId, geneticStatuses}: GetOwnGeneticStatusSuccess
  ) {
    ctx.patchState({
      currentUserId: userId,
      getOwnStatusProgress: PROGRESS_STATUSES.SUCCEED,
      isPublicGenetics: false,
      mostActualOwnStatus: DashboardGeneticsRetrieveState.extractMostActualStatus(geneticStatuses),
    });
  }

  @Action(GetOwnGeneticStatusFail)
  getOwnGeneticStatusFail(ctx: StateContext<DashboardGeneticsRetrieveStateModel>, {error}: GetOwnGeneticStatusFail) {
    ctx.patchState({
      getOwnStatusProgress: PROGRESS_STATUSES.INTERRUPTED,
      lastGetOwnStatusError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(GetGeneticStatus)
  getGeneticStatus(ctx: StateContext<DashboardGeneticsRetrieveStateModel>, {userId, isSubmissionPublic}: GetGeneticStatus) {
    ctx.patchState({
      currentUserId: null,
      isPublicGenetics: null,
      getStatusProgress: PROGRESS_STATUSES.IN_PROGRESS,
      lastGetStatusError: null,
      mostActualStatus: null,
      geneticData: null,
    });

    this.geneticsRetrieve.getGeneticStatuses(userId, isSubmissionPublic).subscribe({
      next: geneticData => ctx.dispatch(new GetGeneticStatusSuccess(userId, isSubmissionPublic, geneticData)),
      error: error => ctx.dispatch(new GetGeneticStatusFail(error)),
    });
  }

  @Action(GetGeneticStatusSuccess)
  getGeneticStatusSuccess(
    ctx: StateContext<DashboardGeneticsRetrieveStateModel>,
    {userId, isSubmissionPublic, geneticStatuses}: GetGeneticStatusSuccess
  ) {
    ctx.patchState({
      currentUserId: userId,
      isPublicGenetics: isSubmissionPublic,
      getStatusProgress: PROGRESS_STATUSES.SUCCEED,
      mostActualStatus: DashboardGeneticsRetrieveState.extractMostActualStatus(geneticStatuses),
    });
  }

  @Action(GetGeneticStatusFail)
  getGeneticStatusFail(ctx: StateContext<DashboardGeneticsRetrieveStateModel>, {error}: GetGeneticStatusFail) {
    ctx.patchState({
      getStatusProgress: PROGRESS_STATUSES.INTERRUPTED,
      lastGetStatusError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(LoadGeneticData)
  loadGeneticData(ctx: StateContext<DashboardGeneticsRetrieveStateModel>) {
    const state = ctx.getState();
    const userId = state.currentUserId;

    // Prevent loading if no user ID is saved.
    if (!userId) {
      return;
    }

    const isSubmissionPublic = state.isPublicGenetics;

    ctx.patchState({
      loadGeneticsProgress: PROGRESS_STATUSES.IN_PROGRESS,
      loadGeneticsError: null,
    });

    this.geneticsRetrieve.loadGeneticData(userId, isSubmissionPublic).subscribe({
      next: geneticData => ctx.dispatch(new LoadGeneticDataSuccess(geneticData)),
      error: error => ctx.dispatch(new LoadGeneticDataFail(error)),
    });
  }

  @Action(LoadGeneticDataSuccess)
  loadGeneticDataSuccess(ctx: StateContext<DashboardGeneticsRetrieveStateModel>, {geneticData}: LoadGeneticDataSuccess) {
    ctx.patchState({
      loadGeneticsProgress: PROGRESS_STATUSES.SUCCEED,
      geneticData,
    });
  }

  @Action(LoadGeneticDataFail)
  loadGeneticDataFail(ctx: StateContext<DashboardGeneticsRetrieveStateModel>, {error}: LoadGeneticDataFail) {
    ctx.patchState({
      loadGeneticsProgress: PROGRESS_STATUSES.INTERRUPTED,
      loadGeneticsError: error,
    });

    ctx.dispatch(new BackendError(error));
  }
}
